import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { company } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    surface: "",
    message: "",
    website: "", // honeypot
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Vul alstublieft alle verplichte velden in",
        description: "Naam, e-mail en bericht zijn nodig.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form, { timeout: 20000 });
      if (res.data && res.data.success) {
        setSuccess(true);
        toast({
          title: "Bedankt voor uw aanvraag!",
          description: "We nemen binnen 24 uur contact met u op.",
        });
        setForm({ name: "", email: "", phone: "", surface: "", message: "", website: "" });
        setTimeout(() => setSuccess(false), 4000);
      } else {
        throw new Error("Onbekende fout");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: "Er ging iets mis",
        description:
          typeof detail === "string"
            ? detail
            : "Probeer het later opnieuw of bel ons direct.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-100 text-slate-800 text-xs font-semibold mb-4 border border-yellow-200">
              Contact
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 text-balance">
              Vrijblijvend offerte aanvragen
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Laat uw gegevens achter en wij nemen binnen 24 uur contact met u op met
              een eerlijke prijsopgave op maat.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={`tel:${company.phoneRaw}`}
                className="flex items-start gap-4 p-4 rounded-xl border border-slate-200 hover:border-yellow-300 hover:bg-yellow-50/50 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-yellow-400 flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-slate-900" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Bel of app gratis</div>
                  <div className="font-bold text-slate-900">{company.phone}</div>
                </div>
              </a>
              <a
                href={`mailto:${company.email}`}
                className="flex items-start gap-4 p-4 rounded-xl border border-slate-200 hover:border-yellow-300 hover:bg-yellow-50/50 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-yellow-400 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-slate-900" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">E-mail</div>
                  <div className="font-bold text-slate-900">{company.email}</div>
                </div>
              </a>
              <div className="flex items-start gap-4 p-4 rounded-xl border border-slate-200">
                <div className="w-11 h-11 rounded-lg bg-yellow-400 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-slate-900" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Werkgebied</div>
                  <div className="font-bold text-slate-900">Heel Nederland</div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8 space-y-4"
            >
              {/* Honeypot field, hidden from users */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex={-1}
                style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
                aria-hidden="true"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Naam *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Uw naam"
                    className="bg-white h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefoon</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="06 - 12 34 56 78"
                    className="bg-white h-11"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-mailadres *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="naam@voorbeeld.nl"
                    className="bg-white h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surface">Oppervlakte (ca. m²)</Label>
                  <Input
                    id="surface"
                    name="surface"
                    value={form.surface}
                    onChange={handleChange}
                    placeholder="bv. 80"
                    className="bg-white h-11"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Uw bericht *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Vertel kort over uw project: wat moet er gespoten worden, locatie, gewenste planning..."
                  rows={5}
                  className="bg-white"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold text-base"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Versturen...
                  </>
                ) : success ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Verzonden!
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Stuur aanvraag
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                Uw gegevens worden alleen gebruikt voor het beantwoorden van uw aanvraag.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
